import {
  Views,
  momentLocalizer,
  Calendar as BigCalendar,
} from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import styled from "styled-components";
import _withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import Flex from "common/Boxes/Flex.tsx";
import { TimeLockOutlined } from "common/Icons/Icons.tsx";
import Text from "common/Typography/Text";
import { StatusCircle } from "Global/StatusCircle.tsx";
import useCalendar from "common/Calendars/useCalendar.ts";
import React, { CSSProperties, Dispatch, SetStateAction } from "react";
import { getBaseDate, getIsAllDayEvent } from "common/Calendars/utils.ts";
import { OperationHoursEvent } from "@shared/types";
import EventPopover from "common/Calendars/EventPopover.tsx";
import moment from "moment";
import useMedia from "utils/hooks/useMedia.ts";
import { hexToRGB } from "@shared/utils/misc.ts";
import { TIME_FORMAT } from "common/Calendars/constants.ts";

const withDragAndDrop = (_withDragAndDrop as any).default || _withDragAndDrop;

interface CalendarProps {
  myEvents: OperationHoursEvent[];
  setMyEvents?: Dispatch<SetStateAction<OperationHoursEvent[] | undefined>>;
  popoverTitle?: string;
  timezone?: string;
  viewOnly?: boolean;
  style?: CSSProperties;
}

const Calendar = ({
  myEvents,
  setMyEvents,
  popoverTitle,
  timezone,
  viewOnly,
  style,
}: CalendarProps) => {
  const localizer = momentLocalizer(moment);
  const smallScreen = useMedia([`(max-width: 1200px)`], [true], false);
  const viewOnlySmallScreen = useMedia([`(max-width: 750px)`], [true], false);
  const isSmallScreen = viewOnly ? viewOnlySmallScreen : smallScreen;
  const hideTimeRange = useMedia([`(max-width: 1000px)`], [true], false);

  const {
    events,
    handleSelectSlot,
    moveEvent,
    timeGutterFormat,
    eventPropGetter,
    allowDragAndResize,
    popoverTarget,
    showPopover,
    setShowPopover,
    selectedEvent,
    setSelectedEvent,
    handleDeleteEvent,
  } = useCalendar({ myEvents, setMyEvents, timezone, viewOnly });

  const CustomEventComponent = ({ event }: { event: OperationHoursEvent }) => {
    const end = moment(event.end);
    const start = moment(event.start);
    const isAllDay = getIsAllDayEvent(event);
    const isSmallEvent = end.diff(start) / 1000 / 60 <= 30;

    return (
      <EventLabel
        id={event.id}
        $isActive={event.isActive}
        $isSmallEvent={isSmallEvent}
      >
        {event.isActive ? (
          <StatusCircle $animated={true} style={{ marginRight: 6 }} />
        ) : (
          <TimeLockIcon
            $isSmallEvent={isSmallEvent || (isSmallScreen && !hideTimeRange)}
          />
        )}
        {!hideTimeRange && (
          <EventTimeRange
            $isActive={event.isActive}
            $isSmallEvent={isSmallEvent || isSmallScreen}
          >
            {isAllDay
              ? "All Day"
              : `${start.format(TIME_FORMAT)} - ${end.format(TIME_FORMAT)}`}
          </EventTimeRange>
        )}
      </EventLabel>
    );
  };

  const CustomDayHeader = ({ date }) => {
    return <DayHeader>{moment(date).format("ddd")}</DayHeader>;
  };

  const padding = isSmallScreen ? 16 : 20;
  const paddingTop = viewOnly ? 0 : padding;

  return (
    <div id="calender-wrapper" style={{ overflow: "auto", height: "100%" }}>
      <div
        style={{
          height: 1240 + padding + paddingTop,
          padding,
          paddingTop,
          ...style,
        }}
      >
        <DnDCalendar
          key="week-calendar"
          defaultView={Views.WEEK}
          views={[Views.WEEK]}
          toolbar={false}
          defaultDate={getBaseDate()}
          step={15}
          timeslots={4}
          events={events}
          localizer={localizer}
          onSelectSlot={handleSelectSlot}
          longPressThreshold={250}
          selectable={viewOnly ? false : "ignoreEvents"}
          formats={{
            timeGutterFormat,
            eventTimeRangeFormat: () => "",
          }}
          onEventDrop={moveEvent}
          onEventResize={moveEvent}
          resizable
          components={{
            event: CustomEventComponent,
            week: {
              header: CustomDayHeader,
            },
          }}
          onSelectEvent={setSelectedEvent}
          eventPropGetter={eventPropGetter}
          draggableAccessor={allowDragAndResize}
          resizableAccessor={allowDragAndResize}
        />
        <EventPopover
          showPopover={showPopover}
          setShowPopover={setShowPopover}
          popoverTarget={popoverTarget}
          popoverTitle={popoverTitle}
          setMyEvents={setMyEvents}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          viewOnly={viewOnly}
          handleDeleteEvent={handleDeleteEvent}
        />
      </div>
    </div>
  );
};

export default Calendar;

const EventTimeRange = styled(Text)<{
  $isActive?: boolean;
  $isSmallEvent?: boolean;
}>`
  font-size: ${(p) =>
    p.$isSmallEvent ? "11px" : p.theme.typography.fontSizes.S};
  color: ${(p) =>
    p.$isActive ? p.theme.colors.primaryBlue : p.theme.colors.primaryGrey};
  flex: 1;
`;
const TimeLockIcon = styled(TimeLockOutlined)<{ $isSmallEvent?: boolean }>`
  margin-right: 4px;
  top: -2px;
  position: relative;
  font-size: ${(p) => (p.$isSmallEvent ? 12 : 14)}px;
  min-width: ${(p) => (p.$isSmallEvent ? 12 : 14)}px;
`;
const EventLabel = styled(Flex)<{
  $isActive?: boolean;
  $isSmallEvent: boolean;
}>`
  justify-content: flex-start;
  align-items: ${(p) => (p.$isActive ? "baseline" : "flex-start")};
  ${(p) =>
    !p.$isSmallEvent &&
    `
    padding-top: 4px;
  `};
`;
const DayHeader = styled(Text)`
  padding: 4px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  height: 60px;
  display: flex;
  align-items: center;
`;
const StyledBigCalendar = styled(BigCalendar)`
  .rbc-time-view,
  .rbc-time-content,
  .rbc-day-bg + .rbc-day-bg,
  .rbc-day-slot .rbc-event,
  .rbc-event.rbc-selected,
  .rbc-day-slot .rbc-time-slot {
    border: none;
  }
  .rbc-timeslot-group {
    border-left: none;
    border-bottom-color: transparent;
  }
  .rbc-allday-cell,
  .rbc-current-time-indicator {
    display: none;
  }
  .rbc-time-header-content {
    border: 1px solid ${(p) => p.theme.colors.lightestBorderGrey};
    border-radius: 12px 12px 0 0;
    border-bottom: none;
  }
  .rbc-day-slot .rbc-time-slot:first-child {
    border-top: ${(p) => p.theme.borders.lightest};
  }
  .rbc-time-content > .rbc-day-slot {
    border-bottom: ${(p) => p.theme.borders.lightest};
    border-left: ${(p) => p.theme.borders.lightest};
  }
  .rbc-time-column:last-child .rbc-timeslot-group {
    border-right: ${(p) => p.theme.borders.lightest};
  }
  .rbc-label {
    padding: 0 10px 0 0;
    color: ${(p) => p.theme.colors.secondaryText};
    font-size: 12px;
  }
  .rbc-day-slot .rbc-events-container {
    margin-right: 0;
  }
  .rbc-header + .rbc-header,
  .rbc-header {
    border-color: ${(p) => p.theme.colors.lightestBorderGrey};
  }
  .rbc-time-header-gutter {
    width: 44px !important;
  }
  .rbc-event {
    width: 100% !important;
    left: 0 !important;
  }
  .rbc-time-slot,
  .rbc-day-slot .rbc-event {
    min-height: 12px;
  }
  .rbc-today {
    background: transparent;
  }
  .rbc-slot-selection {
    background: ${(p) =>
      hexToRGB(p.theme.colors.secondaryGrey, p.theme.isDarkMode ? 0.2 : 0.04)};
    color: ${(p) => p.theme.colors.mediumGrey};
  }
  .rbc-time-column .rbc-timeslot-group {
    flex: unset;
  }
`;

const DnDCalendar = withDragAndDrop(StyledBigCalendar);
